import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FloppyDisk, Check, ClipboardText, Info } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const REFLECTION_QUESTIONS = [
    {
        id: 'voertuig',
        category: 'Omgaan met het voertuig',
        text: 'Onder normale verkeersomstandigheden bedien ik de auto op een technisch juiste wijze en heb ik de auto onder controle.',
    },
    {
        id: 'veiligheid_afstand',
        category: 'Veiligheid',
        text: 'Ik houd voldoende afstand van het verkeer dat voor mij rijdt en ik zorg voor voldoende ruimte rondom de auto.',
    },
    {
        id: 'veiligheid_gevaar',
        category: 'Veiligheid',
        text: 'Mogelijke gevaren herken ik op tijd en ik zorg dat het zo veilig mogelijk blijft.',
    },
    {
        id: 'doorstroming',
        category: 'Doorstroming',
        text: 'Ik hinder het andere verkeer niet onnodig en zorg dat het zoveel mogelijk kan doorrijden.',
    },
    {
        id: 'sociaal_zwakker',
        category: 'Sociaal rijgedrag',
        text: 'Bij het autorijden houd ik rekening met de gedragingen van zwakkere verkeersdeelnemers zoals kinderen, ouderen, voetgangers en fietsers.',
    },
    {
        id: 'sociaal_fouten',
        category: 'Sociaal rijgedrag',
        text: 'Ik houd rekening met het andere verkeer en ik vang fouten van anderen zo goed mogelijk op.',
    },
    {
        id: 'milieu',
        category: 'Milieubewust rijden',
        text: 'Ik weet hoe ik milieubewust moet autorijden en ik pas dat in de praktijk toe.',
    },
];

function scoreColor(score, isActive) {
    if (!score) return '';
    if (score <= 3) return isActive ? 'bg-red-500 border-red-500 text-white' : 'text-red-400';
    if (score <= 6) return isActive ? 'bg-amber-500 border-amber-500 text-[#1A202C]' : 'text-amber-400';
    return isActive ? 'bg-emerald-500 border-emerald-500 text-white' : 'text-emerald-400';
}

export default function ReflectionTab({ studentId, readOnly = false }) {
    const [scores, setScores] = useState({});
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [examDate, setExamDate] = useState('');
    const [updatedAt, setUpdatedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await axios.get(`${API}/students/${studentId}/reflection`, { withCredentials: true });
                if (!mounted) return;
                setScores(res.data.scores || {});
                setDateOfBirth(res.data.date_of_birth || '');
                setExamDate(res.data.exam_date || '');
                setUpdatedAt(res.data.updated_at || null);
                setHasChanges(false);
            } catch (e) {
                console.error('load reflection', e);
            }
        };
        load();
        return () => { mounted = false; };
    }, [studentId]);

    const handleScore = (qId, value) => {
        if (readOnly) return;
        setScores(prev => ({ ...prev, [qId]: prev[qId] === value ? 0 : value }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveMsg('');
        try {
            // Strip 0 scores before submitting
            const cleanScores = Object.fromEntries(
                Object.entries(scores).filter(([, v]) => v >= 1 && v <= 10)
            );
            const res = await axios.put(`${API}/students/me/reflection`, {
                date_of_birth: dateOfBirth || null,
                exam_date: examDate || null,
                scores: cleanScores,
            }, { withCredentials: true });
            setUpdatedAt(res.data.updated_at);
            setHasChanges(false);
            setSaveMsg('Opgeslagen!');
            setTimeout(() => setSaveMsg(''), 3000);
        } catch (e) {
            setSaveMsg(e.response?.data?.detail || 'Fout bij opslaan');
        } finally {
            setSaving(false);
        }
    };

    const answeredCount = Object.values(scores).filter(v => v >= 1 && v <= 10).length;
    const avg = answeredCount > 0
        ? (Object.values(scores).filter(v => v >= 1).reduce((a, b) => a + b, 0) / answeredCount).toFixed(1)
        : null;

    return (
        <div className="space-y-4">
            <Card className="bg-[#2D3748] border-[#4A5568]" data-testid="reflection-tab">
                <CardHeader>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                <ClipboardText size={22} className="text-[#D4A537]" weight="fill" />
                                Zelfreflectie
                            </CardTitle>
                            <p className="text-sm text-gray-400 mt-1">
                                {readOnly
                                    ? 'Dit is de zelfreflectie zoals ingevuld door de leerling.'
                                    : 'Beoordeel hoe goed je bent in onderstaande vaardigheden: 1 = slecht, 10 = uitstekend.'}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {avg && (
                                <div className="text-right">
                                    <div className="text-xs text-gray-500 uppercase tracking-wider">Gemiddelde</div>
                                    <div className={`text-2xl font-bold ${avg >= 7 ? 'text-emerald-400' : avg >= 4 ? 'text-amber-400' : 'text-red-400'}`}>
                                        {avg}
                                    </div>
                                </div>
                            )}
                            {updatedAt && readOnly && (
                                <div className="text-right">
                                    <div className="text-xs text-gray-500 uppercase tracking-wider">Laatst ingevuld</div>
                                    <div className="text-sm text-gray-300">{new Date(updatedAt).toLocaleDateString('nl-NL')}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Persoonlijke/examen gegevens */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-[#1A202C] rounded-lg border border-[#4A5568]">
                        <div className="space-y-1.5">
                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Geboortedatum</Label>
                            <Input
                                type="date"
                                value={dateOfBirth}
                                onChange={(e) => { setDateOfBirth(e.target.value); setHasChanges(true); }}
                                disabled={readOnly}
                                className="bg-[#2D3748] border-[#4A5568] text-white disabled:opacity-70"
                                data-testid="reflection-dob"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Datum examen/toets</Label>
                            <Input
                                type="date"
                                value={examDate}
                                onChange={(e) => { setExamDate(e.target.value); setHasChanges(true); }}
                                disabled={readOnly}
                                className="bg-[#2D3748] border-[#4A5568] text-white disabled:opacity-70"
                                data-testid="reflection-examdate"
                            />
                        </div>
                    </div>

                    {!readOnly && (
                        <div className="flex items-start gap-2 p-3 rounded-md border border-[#D4A537]/30 bg-[#D4A537]/5 text-sm text-[#D4A537]">
                            <Info size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
                            <div>
                                Kruis hieronder aan hoe goed je bent in de betreffende vaardigheden:
                                <strong className="text-red-400 ml-1">1 = slecht</strong>,
                                <strong className="text-emerald-400 ml-1">10 = uitstekend</strong>.
                            </div>
                        </div>
                    )}

                    {/* Questions */}
                    <div className="space-y-4">
                        {REFLECTION_QUESTIONS.map((q, idx) => {
                            const currentScore = scores[q.id] || 0;
                            return (
                                <div key={q.id} className="p-4 bg-[#1A202C] rounded-lg border border-[#4A5568]" data-testid={`reflection-q-${q.id}`}>
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#D4A537] text-[#1A202C] flex items-center justify-center font-bold text-sm">
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-[#D4A537] uppercase tracking-wider mb-1">
                                                {q.category}
                                            </div>
                                            <p className="text-white text-sm leading-relaxed">{q.text}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-10 gap-1.5">
                                        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => {
                                            const isActive = currentScore === n;
                                            return (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    onClick={() => handleScore(q.id, n)}
                                                    disabled={readOnly}
                                                    className={`h-10 rounded-md border-2 font-bold text-sm transition-all ${
                                                        isActive
                                                            ? scoreColor(n, true)
                                                            : `border-[#4A5568] bg-[#2D3748] ${scoreColor(n, false)} ${!readOnly ? 'hover:border-[#D4A537]/60' : ''}`
                                                    } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                                                    data-testid={`reflection-${q.id}-score-${n}`}
                                                >
                                                    {n}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Save bar */}
                    {!readOnly && (
                        <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-[#1A202C] border-t border-[#4A5568] flex items-center justify-between">
                            <div className="text-sm text-gray-400">
                                <span className="text-white font-semibold">{answeredCount}</span> / {REFLECTION_QUESTIONS.length} vragen ingevuld
                            </div>
                            <div className="flex items-center gap-3">
                                {saveMsg && (
                                    <span className={`text-sm flex items-center gap-1 ${saveMsg === 'Opgeslagen!' ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {saveMsg === 'Opgeslagen!' && <Check size={16} />}
                                        {saveMsg}
                                    </span>
                                )}
                                <Button
                                    onClick={handleSave}
                                    disabled={saving || !hasChanges}
                                    className="bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold disabled:opacity-50"
                                    data-testid="reflection-save-button"
                                >
                                    <FloppyDisk size={16} className="mr-1" />
                                    {saving ? 'Opslaan...' : 'Opslaan'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {readOnly && answeredCount === 0 && (
                        <div className="text-center py-8 text-gray-500 text-sm">
                            Deze leerling heeft nog geen zelfreflectie ingevuld.
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
